import React from 'react';
import { Event } from '../../types/Event';
import { getLocationDisplayName } from '../../utils/locationUtils';
import { TableColumn } from '../../utils/tableUtils';
import { StatusBadge } from '../shared/AdminTable';
import { Edit, Trash2 } from 'lucide-react';

// Helper function to get event timing status
const getEventTiming = (event: Event): string => {
  const now = new Date();
  const start = new Date(event.startDateTime);
  const end = new Date(event.endDateTime);

  if (now < start) return 'Upcoming';
  if (now >= start && now <= end) return 'Ongoing';
  return 'Past';
};

// Define event table columns
export const eventColumns: TableColumn<Event>[] = [
  {
    id: 'title',
    label: 'Title',
    accessor: (event) => event.title,
    sortable: true,
  },
  {
    id: 'description',
    label: 'Description',
    accessor: (event) => (
      <div className="max-w-xs truncate" title={event.description}>
        {event.description}
      </div>
    ),
    sortable: true,
  },
  {
    id: 'startDateTime',
    label: 'Start Time',
    accessor: (event) => new Date(event.startDateTime).toLocaleString(),
    sortable: true,
  },
  {
    id: 'endDateTime',
    label: 'End Time',
    accessor: (event) => new Date(event.endDateTime).toLocaleString(),
    sortable: true,
  },
  {
    id: 'location',
    label: 'Location',
    accessor: (event) => getLocationDisplayName(event.location),
    sortable: true,
  },
  {
    id: 'volunteersNeeded',
    label: 'Volunteers',
    accessor: (event) => (
      <StatusBadge
        status={event.volunteersNeeded ? 'Needed' : 'Not Needed'}
        type="volunteers"
      />
    ),
    sortable: true,
  },
  {
    id: 'timing',
    label: 'Status',
    accessor: (event) => (
      <StatusBadge
        status={getEventTiming(event)}
        type="timing"
      />
    ),
    sortable: true,
  },
  {
    id: 'actions',
    label: 'Actions',
    accessor: () => null, // Actions are handled by the AdminTable component
    sortable: false,
  },
];

// Event table configuration
export const eventTableConfig = {
  entityName: 'Event',
  apiEndpoint: '/api/public/events',
  columns: eventColumns,
  searchFields: ['title', 'description', 'location'] as (keyof Event)[],
  filterOptions: {
    volunteersNeeded: [
      { value: 'needed', label: 'Volunteers Needed' },
      { value: 'not_needed', label: 'No Volunteers Needed' }
    ],
    timing: [
      { value: 'Upcoming', label: 'Upcoming' },
      { value: 'Ongoing', label: 'Ongoing' },
      { value: 'Past', label: 'Past' }
    ],
    location: [] as { value: string; label: string }[] // Fix type annotation
  },
  exportFileName: 'events',
  defaultVisibleColumns: {
    title: true,
    description: true,
    startDateTime: true,
    endDateTime: true,
    location: true,
    volunteersNeeded: true,
    timing: true,
    actions: true
  }
};
